import { moveConnectedEdges } from "../../utils/moveCells";
import { mxConnectionHandler, mxGraph, mxConstants, mxGuide, mxEdgeHandler,
    mxGraphView, mxPoint, mxConstraintHandler, mxEllipse, 
    mxEdgeSegmentHandler, mxCellHighlight, mxEdgeStyle, mxStyleRegistry,
    mxCellEditor, mxEvent, mxGraphHandler, mxRubberband, mxUtils } from "./mxModule";

// const mxModule = require("mxgraph")();
// const { mxCellEditor, mxEvent, mxGraphHandler, mxRubberband, mxUtils } =
//   mxModule;

function setDefaultStylesForVertexAndEdge(graph) {
  var joinNodeSize = 7;
  var strokeWidth = 1;
  // Switch for black background and bright styles
  var invert = false;
  if (invert) {
    // container.style.backgroundColor = 'black';

    // White in-place editor text color
    let mxCellEditorStartEditing = mxCellEditor.prototype.startEditing;
    mxCellEditor.prototype.startEditing = function (cell, trigger) {
      mxCellEditorStartEditing.apply(this, arguments);

      if (this.textarea != null) {
        this.textarea.style.color = "#FFFFFF";
      }
    };

    mxGraphHandler.prototype.previewColor = "white";
  }

  var labelBackground = invert ? "#000000" : "#FFFFFF";
  var fontColor = invert ? "#FFFFFF" : "#000000";
  // var strokeColor = invert ? "#C0C0C0" : "#000000";

  var fillColor = invert ? "none" : "#FFFFFF";

  var style = graph.getStylesheet().getDefaultEdgeStyle();
  const edgeStrokeColor = "#38befe";
  delete style["endArrow"];
  style["strokeColor"] = edgeStrokeColor;
  style["labelBackgroundColor"] = labelBackground;
  style["edgeStyle"] = "wireEdgeStyle";
  style["fontColor"] = fontColor;
  style["fontSize"] = "9";
  style["movable"] = "0";
  style["strokeWidth"] = strokeWidth;
  style["orthogonalLoop"] = "1"; //to allow the connections of same vertex connection points
  //style['rounded'] = '1';

  // Sets join node size
  style["startSize"] = joinNodeSize;
  style["endSize"] = joinNodeSize;

  style = graph.getStylesheet().getDefaultVertexStyle();
  var vertexStrokeColor = "#00cc66";
  style["gradientDirection"] = "south";
  //style['gradientColor'] = '#909090';
  style["strokeColor"] = vertexStrokeColor;
  //style['fillColor'] = '#e0e0e0';
  style["fillColor"] = "none";
  style["fontColor"] = fontColor;
  style["fontStyle"] = "1";
  style["fontSize"] = "12";
  style["resizable"] = "0";
  style["rounded"] = "1";
  style["strokeWidth"] = strokeWidth;
}

function buildGraphObject(graph) {
  setDefaultStylesForVertexAndEdge(graph);

  // Port child cells (parent is a shape vertex) must not be deletable
  graph.isCellDeletable = function (cell) {
    const parent = this.model.getParent(cell);
    if (parent && this.model.isVertex(parent)) return false;
    return true;
  };

  graph.dropEnabled = true;
  graph.setConnectable(true);
  graph.setMultigraph(true);

  // MyToolBarCheck(graph, toolbarRef.current);

  graph.view.scale = 1;
  graph.setPanning(true);
  graph.setTooltips(true);
  graph.setConnectableEdges(true);
  graph.setDisconnectOnMove(false);
  graph.foldingEnabled = false;
  graph.setAllowNegativeCoordinates(false); //not allowing the instances to move negative co-ordinates
  graph.graphHandler.previewEnabled = true;
  graph.graphHandler.guidesEnabled = true; // Optional alignment guides
  graph.graphHandler.highlightDropTargets = true;
  
  // Moving cell Preview
  graph.graphHandler.allowLivePreview = true;

  // 2. Set limit (IMPORTANT - default is 0 → disables preview)
  graph.graphHandler.maxLivePreview = 10000;

  // 3. Force enable
  graph.graphHandler.isLivePreview = function () {
    return true;
  };

  graph.graphHandler.highlightEnabled = false;

  // Prevent stale connected edge highlight during live-preview vertex drag.
  const setSelectionEdgeShapesVisible = (visible) => {
    graph.selectionCellsHandler.handlers.visit((key, handler) => {
      if (handler?.shape?.node && graph.getModel().isEdge(handler.state.cell)) {
        handler.shape.node.style.visibility = visible ? "" : "hidden";
      }
    });
  };

  const shiftConnectedWaypointEdges = (handler, dx, dy) => {
    const model = graph.getModel();
    const shifted = [];
    const edges = new Set();

    const collectConnectedEdges = (cell) => {
      model.getEdges(cell)?.forEach((edge) => {
        const geo = model.getGeometry(edge);
        if (!handler.cells.includes(edge) && geo?.points?.length) {
          edges.add(edge);
        }
      });

      const childCount = model.getChildCount(cell);
      for (let i = 0; i < childCount; i += 1) {
        collectConnectedEdges(model.getChildAt(cell, i));
      }
    };

    handler.cells.forEach((cell) => {
      if (model.isVertex(cell)) {
        collectConnectedEdges(cell);
      }
    });

    edges.forEach((edge) => {
      const geo = model.getGeometry(edge);
      shifted.push([geo, geo.points]);
      geo.points = geo.points.map((pt) => new mxPoint(pt.x + dx, pt.y + dy));
    });

    return () => {
      shifted.forEach(([geo, points]) => {
        geo.points = points;
      });
    };
  };

  const originalUpdatePreview = graph.graphHandler.updatePreview.bind(graph.graphHandler);
  graph.graphHandler.updatePreview = function () {
    const movingVertices = this.cells?.some((cell) => graph.getModel().isVertex(cell));
    const scale = graph.view.scale;
    const restore =
      this.livePreviewUsed && movingVertices && this.currentDx != null && this.currentDy != null
        ? shiftConnectedWaypointEdges(this, this.currentDx / scale, this.currentDy / scale)
        : null;

    try {
      originalUpdatePreview();
    } finally {
      restore?.();
    }

    if (this.livePreviewUsed && movingVertices) {
      setSelectionEdgeShapesVisible(false);
    }
  };

  const originalReset = graph.graphHandler.reset.bind(graph.graphHandler);
  graph.graphHandler.reset = function () {
    setSelectionEdgeShapesVisible(true);
    originalReset();
  };


  graph.setAllowLoops(true); //to allow the connections of same vertex connection points
  //Maximum size

  //commented to keep the graph height grow when the container height grows.
  // graph.maximumGraphBounds = new mxRectangle(
  //   0,
  //   0,
  //   containerRef.current.clientWidth,
  //   containerRef.current.clientHeight
  // );

  // Increase tolerance for edge hit detection
  // Default tolerance is 4 — increase to make thin edges easier to hover/click
  graph.tolerance = 8;

  const _originalGetCellAt = graph.getCellAt.bind(graph);
  graph.getCellAt = function(x, y, parent, vertices, edges) {
    let cell = _originalGetCellAt(x, y, parent, vertices, edges);
    if (cell) return cell;

    const tol = 8;
    const cells = graph.getChildCells(
      parent || graph.getDefaultParent(), false, true
    );

    for (const edge of cells) {
      const state = graph.view.getState(edge);
      if (!state || !state.absolutePoints) continue;

      const pts = state.absolutePoints;
      for (let i = 0; i < pts.length - 1; i++) {
        const p1 = pts[i];
        const p2 = pts[i + 1];
        if (!p1 || !p2) continue;

        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const lenSq = dx * dx + dy * dy;
        if (lenSq === 0) continue;

        let t = ((x - p1.x) * dx + (y - p1.y) * dy) / lenSq;
        t = Math.max(0, Math.min(1, t));

        const nearX = p1.x + t * dx;
        const nearY = p1.y + t * dy;
        const dist = Math.sqrt(
          (x - nearX) * (x - nearX) + (y - nearY) * (y - nearY)
        );

        if (dist <= tol) return edge;
      }
    }

    return null;
  };

  graph.border = 50;

  // Panning handler consumed right click so this must be
  // disabled if right click should stop connection handler.
  graph.panningHandler.isPopupTrigger = function () {
    return false;
  };

  // Enables return key to stop editing (use shift-enter for newlines)
  graph.setEnterStopsCellEditing(true);

  // Adds rubberband selection
  new mxRubberband(graph);

  // Alternative solution for implementing connection points without child cells.
  // This can be extended as shown in portrefs.html example to allow for per-port
  // incoming/outgoing direction.
  graph.getAllConnectionConstraints = function (terminal) {
    if (
      terminal != null &&
      terminal.shape != null &&
      terminal.shape.stencil == null &&
      terminal.shape.constraints != null
    ) {
      return terminal.shape.constraints;
    }
  };

  // Makes sure non-relative cells can only be connected via constraints
  graph.connectionHandler.isConnectableCell = function (cell) {
    if (this.graph.getModel().isEdge(cell)) {
      return true;
    } else {
      var geo = cell != null ? this.graph.getCellGeometry(cell) : null;

      return geo != null ? geo.relative : false;
    }
  };

  graph.connectionHandler.marker.highlightColor = "red"; //phye logo color to be changed

  // Adds a special tooltip for edges

  var getTooltipForCell = graph.getTooltipForCell;
  graph.getTooltipForCell = function (cell) {
    var tip = "";

    if (cell != null) {
      var src = this.getModel().getTerminal(cell, true);

      if (src != null) {
        tip += this.getTooltipForCell(src) + " ";
      }

      var parent = this.getModel().getParent(cell);

      if (this.getModel().isVertex(parent)) {
        tip += this.getTooltipForCell(parent) + ".";
      }

      tip += getTooltipForCell.apply(this, arguments);

      var trg = this.getModel().getTerminal(cell, false);

      if (trg != null) {
        tip += " " + this.getTooltipForCell(trg);
      }
    }

    return tip;
  };

  graph.addListener(mxEvent.CELLS_MOVED, function (sender, evt) {
    const movedCells = evt.getProperty("cells");
    const dx = evt.getProperty("dx");
    const dy = evt.getProperty("dy");

    graph.getModel().beginUpdate();
    try {
      moveConnectedEdges(graph, movedCells, dx, dy);
    } finally {
      graph.getModel().endUpdate();
    }
  });

  graph.view.addListener(mxEvent.SCALE, function (sender, evt) {
    if (graph?.view_type_id === 3) return;
    const scale = graph.view.scale;
    updateEdgeStrokeWidths(scale);
    updateDefaultEdgeStyle(scale);
  });

  // When a new edge is drawn or a cell is added
  graph.getModel().addListener(mxEvent.ADD_CELLS, (sender, evt) => {
    const addedCells = evt.getProperty("cells");
    const scale = graph.view.scale;

    if (addedCells && addedCells.length > 0) {
      graph.getModel().beginUpdate();
      try {
        addedCells.forEach((cell) => {
          if (graph.getModel().isEdge(cell)) {
            const desiredWidth = 1;
            const adjustedWidth = desiredWidth / scale;
            let style = graph.getModel().getStyle(cell) || "";
            style = mxUtils.setStyle(style, "strokeWidth", adjustedWidth);
            graph.getModel().setStyle(cell, style);
          }
        });
      } finally {
        graph.getModel().endUpdate();
      }
    }
  });

  // Optional: update default edge style so new edges start with correct stroke width
  function updateDefaultEdgeStyle(scale) {
    const baseStrokeWidth = 1;
    const adjusted = baseStrokeWidth / scale;
    const edgeStyle = graph.getStylesheet().getDefaultEdgeStyle();
    edgeStyle["strokeWidth"] = adjusted;
  }

  function updateEdgeStrokeWidths(scale) {
    const model = graph.getModel();
    const parent = graph.getDefaultParent();
    const cells = graph.getChildCells(parent, false, true); // Only edges

    model.beginUpdate();
    try {
      for (let i = 0; i < cells.length; i++) {
        const cell = cells[i];
        if (model.isEdge(cell)) {
          const desiredWidth = 1; // Stroke width at 100% zoom
          const adjustedWidth = desiredWidth / scale;

          let style = model.getStyle(cell) || "";
          style = mxUtils.setStyle(style, "strokeWidth", adjustedWidth);
          model.setStyle(cell, style);
        }
      }
    } finally {
      model.endUpdate();
    }
  }
  // mxEvent.disableContextMenu(containerRefs.current[activeTab]);  //Error while removing default graph rendering
}

function mxGuideInit() {
  // Alt disables guides
  mxGuide.prototype.isEnabledForEvent = function (evt) {
    return !mxEvent.isAltDown(evt);
  };
}

function mxConnectionHandlerInit( selectedEdgeColorRef ,selectedEdgeWidthRef ) {
  mxConnectionHandler.prototype.movePreviewAway = false;
  mxConnectionHandler.prototype.waypointsEnabled = true;
  mxConnectionHandler.prototype.getEdgeColor = function (valid) {
    return "#FF0000"; // Red color
  };

  // Sets source terminal point for edge-to-edge connections.
  mxConnectionHandler.prototype.createEdgeState = function (me) {
    var edge = this.graph.createEdge();
    let style = "";
    const strokeColor = selectedEdgeColorRef?.current || "#38befe"; // fallback color
    const strokeWidth = selectedEdgeWidthRef?.current || "2"; // fallback width

    style += `strokeColor=${strokeColor};`;
    style += `strokeWidth=${strokeWidth};`;
    if (this.sourceConstraint != null && this.previous != null) {
      edge.style =
        mxConstants.STYLE_EXIT_X + "=" + this.sourceConstraint.point.x + ";" +
        mxConstants.STYLE_EXIT_Y + "=" + this.sourceConstraint.point.y + ";" + style;
    } else if (this.graph.model.isEdge(me.getCell())) {
      var scale = this.graph.view.scale;
      var tr = this.graph.view.translate;
      var pt = new mxPoint(
        this.graph.snap(me.getGraphX() / scale) - tr.x,
        this.graph.snap(me.getGraphY() / scale) - tr.y
      );
      edge.geometry.setTerminalPoint(pt, true);
      edge.style = style;
    }
    else {
      edge.style = style;
    }
    return this.graph.view.createState(edge);
  };

  // Uses right mouse button to create edges on background
  mxConnectionHandler.prototype.isStopEvent = function (me) {
    return me.getState() != null || mxEvent.isRightMouseButton(me.getEvent());
  };

  // Updates target terminal point for edge-to-edge connections.
  //let mxConnectionHandlerUpdateCurrentState = mxConnectionHandler.prototype.updateCurrentState;
  mxConnectionHandler.prototype.updateCurrentState = function (me) {
    //mxConnectionHandlerUpdateCurrentState.apply(this, arguments);

    this.constraintHandler.update(
      me,
      this.first == null,
      false,
      this.first == null || me.isSource(this.marker.highlight.shape)
        ? null
        : point
    );

    if (
      this.constraintHandler.currentFocus != null &&
      this.constraintHandler.currentConstraint != null
    ) {
      // Handles special case where grid is large and connection point is at actual point in which
      // case the outline is not followed as long as we're < gridSize / 2 away from that point
      if (
        this.marker.highlight != null &&
        this.marker.highlight.state != null &&
        this.marker.highlight.state.cell ==
          this.constraintHandler.currentFocus.cell
      ) {
        //if block injected by Rajesh - Apr 03, 2025 - to handle undefined issue while
        // highlight is enabled on dropped vertices.
        if (this.marker.highlight.shape === undefined) {
          this.marker.highlight.repaint();
        }
        // Direct repaint needed if cell already highlighted
        else if (this.marker.highlight.shape.stroke != "transparent") {
          this.marker.highlight.shape.stroke = "transparent";
          this.marker.highlight.repaint();
        }
      } else {
        this.marker.markCell(
          this.constraintHandler.currentFocus.cell,
          "transparent"
        );
      }

      // Updates validation state
      if (this.previous != null) {
        this.error = this.validateConnection(
          this.previous.cell,
          this.constraintHandler.currentFocus.cell
        );

        if (this.error == null) {
          this.currentState = this.constraintHandler.currentFocus;
        }

        if (
          this.error != null ||
          (this.currentState != null &&
            !this.isCellEnabled(this.currentState.cell))
        ) {
          this.constraintHandler.reset();
        }
      }
    } else {
      if (this.graph.isIgnoreTerminalEvent(me.getEvent())) {
        this.marker.reset();
        this.currentState = null;
      } else {
        this.marker.process(me);
        this.currentState = this.marker.getValidState();
      }

      if (
        this.currentState != null &&
        !this.isCellEnabled(this.currentState.cell)
      ) {
        this.constraintHandler.reset();
        this.marker.reset();
        this.currentState = null;
      }

      var outline = this.isOutlineConnectEvent(me);

      if (this.currentState != null && outline) {
        // Handles special case where mouse is on outline away from actual end point
        // in which case the grid is ignored and mouse point is used instead
        if (me.isSource(this.marker.highlight.shape)) {
          var point = new mxPoint(me.getGraphX(), me.getGraphY());
        }

        var constraint = this.graph.getOutlineConstraint(
          point,
          this.currentState,
          me
        );
        this.constraintHandler.setFocus(me, this.currentState, false);
        this.constraintHandler.currentConstraint = constraint;
        this.constraintHandler.currentPoint = point;
      }

      if (this.outlineConnect) {
        if (
          this.marker.highlight != null &&
          this.marker.highlight.shape != null
        ) {
          var s = this.graph.view.scale;

          if (
            this.constraintHandler.currentConstraint != null &&
            this.constraintHandler.currentFocus != null
          ) {
            this.marker.highlight.shape.stroke =
              mxConstants.OUTLINE_HIGHLIGHT_COLOR;
            this.marker.highlight.shape.strokewidth =
              mxConstants.OUTLINE_HIGHLIGHT_STROKEWIDTH / s / s;
            this.marker.highlight.repaint();
          } else if (this.marker.hasValidState()) {
            // Handles special case where actual end point of edge and current mouse point
            // are not equal (due to grid snapping) and there is no hit on shape or highlight
            // but ignores cases where parent is used for non-connectable child cells
            if (
              this.graph.isCellConnectable(me.getCell()) &&
              this.marker.getValidState() != me.getState()
            ) {
              this.marker.highlight.shape.stroke = "transparent";
              this.currentState = null;
            } else {
              this.marker.highlight.shape.stroke =
                mxConstants.DEFAULT_VALID_COLOR;
            }

            this.marker.highlight.shape.strokewidth =
              mxConstants.HIGHLIGHT_STROKEWIDTH / s / s;
            this.marker.highlight.repaint();
          }
        }
      }
    }

    if (this.edgeState != null) {
      this.edgeState.cell.geometry.setTerminalPoint(null, false);

      if (
        this.shape != null &&
        this.currentState != null &&
        this.currentState.view.graph.model.isEdge(this.currentState.cell)
      ) {
        var scale = this.graph.view.scale;
        var tr = this.graph.view.translate;
        var pt = new mxPoint(
          this.graph.snap(me.getGraphX() / scale) - tr.x,
          this.graph.snap(me.getGraphY() / scale) - tr.y
        );
        this.edgeState.cell.geometry.setTerminalPoint(pt, false);
      }
    }
  };

  let mxConnectionHandlerCreateMarker =
    mxConnectionHandler.prototype.createMarker;
  mxConnectionHandler.prototype.createMarker = function () {
    var marker = mxConnectionHandlerCreateMarker.apply(this, arguments);

    // Uses complete area of cell for new connections (no hotspot)
    marker.intersects = function (state, evt) {
      return true;
    };

    // Adds in-place highlighting
    let mxCellHighlightHighlight = mxCellHighlight.prototype.highlight;
    marker.highlight.highlight = function (state) {
      if (this.state != state) {
        if (this.state != null) {
          this.state.style = this.lastStyle;

          // Workaround for shape using current stroke width if no strokewidth defined
          this.state.style["strokeWidth"] =
            this.state.style["strokeWidth"] || "1";
          this.state.style["strokeColor"] =
            this.state.style["strokeColor"] || "none";

          if (this.state.shape != null) {
            this.state.view.graph.cellRenderer.configureShape(this.state);
            this.state.shape.redraw();
          }
        }

        if (state != null) {
          this.lastStyle = state.style;
          state.style = mxUtils.clone(state.style);
          state.style["strokeColor"] = "#00ff00";
          state.style["strokeWidth"] = "2";
          if (state.shape != null) {
            state.view.graph.cellRenderer.configureShape(state);
            state.shape.redraw();
          }
        }

        this.state = state;
      }
    };

    return marker;
  };
}

function mxEdgeHandlerInit(graph) {
  // Enables snapping waypoints to terminals
  mxEdgeHandler.prototype.snapToTerminals = true;

  mxEdgeHandler.prototype.isConnectableCell = function (cell) {
    return graph.connectionHandler.isConnectableCell(cell);
  };

  var mxEdgeHandlerConnect = mxEdgeHandler.prototype.connect;
  mxEdgeHandler.prototype.connect = function (
    edge,
    terminal,
    isSource,
    isClone,
    me
  ) {
    var result = null;
    var model = this.graph.getModel();
    var parent = model.getParent(edge);
    model.beginUpdate();
    try {
      result = mxEdgeHandlerConnect.apply(this, arguments);
      var geo = model.getGeometry(result);

      if (geo != null) {
        geo = geo.clone();
        var pt = null;

        if (model.isEdge(terminal)) {
          pt = this.abspoints[this.isSource ? 0 : this.abspoints.length - 1];
          pt.x = pt.x / this.graph.view.scale - this.graph.view.translate.x;
          pt.y = pt.y / this.graph.view.scale - this.graph.view.translate.y;

          var pstate = this.graph
            .getView()
            .getState(this.graph.getModel().getParent(edge));

          if (pstate != null) {
            pt.x -= pstate.origin.x;
            pt.y -= pstate.origin.y;
          }

          pt.x -= this.graph.panDx / this.graph.view.scale;
          pt.y -= this.graph.panDy / this.graph.view.scale;
        }

        geo.setTerminalPoint(pt, isSource);
        model.setGeometry(edge, geo);
      }
    } finally {
      model.endUpdate();
    }

    return result;
  };

  let mxEdgeHandlerCreateMarker = mxEdgeHandler.prototype.createMarker;
  mxEdgeHandler.prototype.createMarker = function () {
    var marker = mxEdgeHandlerCreateMarker.apply(this, arguments);

    // Adds in-place highlighting when reconnecting existing edges
    marker.highlight.highlight =
      this.graph.connectionHandler.marker.highlight.highlight;

    return marker;
  };

  /**
   * Function: getPreviewTerminalState
   *
   * Updates the given preview state taking into account the state of the constraint handler.
   */
  mxEdgeHandler.prototype.getPreviewTerminalState = function (me) {
    this.constraintHandler.update(
      me,
      this.isSource,
      true,
      me.isSource(this.marker.highlight.shape) ? null : this.currentPoint
    );

    if (
      this.constraintHandler.currentFocus != null &&
      this.constraintHandler.currentConstraint != null
    ) {
      // Handles special case where grid is large and connection point is at actual point in which
      // case the outline is not followed as long as we're < gridSize / 2 away from that point
      if (
        this.marker.highlight != null &&
        this.marker.highlight.state != null &&
        this.marker.highlight.state.cell ==
          this.constraintHandler.currentFocus.cell
      ) {
        // // Direct repaint needed if cell already highlighted
        // if (this.marker.highlight.shape.stroke != 'transparent')
        // {
        // 	this.marker.highlight.shape.stroke = 'transparent';
        // 	this.marker.highlight.repaint();
        // }

        //if block injected by Rajesh - Apr 03, 2025 - to handle undefined issue while
        // highlight is enabled on dropped vertices.
        if (this.marker.highlight.shape === undefined) {
          this.marker.highlight.repaint();
        }
        // Direct repaint needed if cell already highlighted
        else if (this.marker.highlight.shape.stroke != "transparent") {
          this.marker.highlight.shape.stroke = "transparent";
          this.marker.highlight.repaint();
        }
      } else {
        this.marker.markCell(
          this.constraintHandler.currentFocus.cell,
          "transparent"
        );
      }

      var model = this.graph.getModel();
      var other = this.graph.view.getTerminalPort(
        this.state,
        this.graph.view.getState(
          model.getTerminal(this.state.cell, !this.isSource)
        ),
        !this.isSource
      );
      var otherCell = other != null ? other.cell : null;
      var source = this.isSource
        ? this.constraintHandler.currentFocus.cell
        : otherCell;
      var target = this.isSource
        ? otherCell
        : this.constraintHandler.currentFocus.cell;

      // Updates the error message of the handler
      this.error = this.validateConnection(source, target);
      var result = null;

      if (this.error == null) {
        result = this.constraintHandler.currentFocus;
      }

      if (
        this.error != null ||
        (result != null && !this.isCellEnabled(result.cell))
      ) {
        this.constraintHandler.reset();
      }

      return result;
    } else if (!this.graph.isIgnoreTerminalEvent(me.getEvent())) {
      this.marker.process(me);
      var state = this.marker.getValidState();

      if (state != null && !this.isCellEnabled(state.cell)) {
        this.constraintHandler.reset();
        this.marker.reset();
      }

      return this.marker.getValidState();
    } else {
      this.marker.reset();

      return null;
    }
  };
}

function mxGraphInit() {
  mxGraph.prototype.resetEdgesOnConnect = false;

  let mxGraphGetCellStyle = mxGraph.prototype.getCellStyle;
  mxGraph.prototype.getCellStyle = function (cell) {
    var style = mxGraphGetCellStyle.apply(this, arguments);

    if (style != null && this.model.isEdge(cell)) {
      style = mxUtils.clone(style);

      if (this.model.isEdge(this.model.getTerminal(cell, true))) {
        style["startArrow"] = "oval";
      }

      if (this.model.isEdge(this.model.getTerminal(cell, false))) {
        style["endArrow"] = "oval";
      }
    }

    return style;
  };

  // This connector needs an mxEdgeSegmentHandler
  let mxGraphCreateHandler = mxGraph.prototype.createHandler;
  mxGraph.prototype.createHandler = function (state) {
    if (state != null) {
      if (this.model.isEdge(state.cell)) {
        var style = this.view.getEdgeStyle(state);

        if (style == mxEdgeStyle.WireConnector) {
          return new mxEdgeSegmentHandler(state);
        }
      }
    }

    return mxGraphCreateHandler.apply(this, arguments);
  };
}

function mxGraphViewInit() {
  // Computes the position of edge to edge connection points.
  mxGraphView.prototype.updateFixedTerminalPoint = function (
    edge,
    terminal,
    source,
    constraint
  ) {
    var pt = null;
    if (constraint != null) {
      pt = this.graph.getConnectionPoint(terminal, constraint);
    }

    if (source) {
      edge.sourceSegment = null;
    } else {
      edge.targetSegment = null;
    }

    if (pt == null) {
      var s = this.scale;
      var tr = this.translate;
      var orig = edge.origin;
      var geo = this.graph.getCellGeometry(edge.cell);
      pt = geo.getTerminalPoint(source);

      // Computes edge-to-edge connection point
      if (pt != null) {
        pt = new mxPoint(
          s * (tr.x + pt.x + orig.x),
          s * (tr.y + pt.y + orig.y)
        );

        // Finds nearest segment on edge and computes intersection
        if (terminal != null && terminal.absolutePoints != null) {
          var seg = mxUtils.findNearestSegment(terminal, pt.x, pt.y);

          // Finds orientation of the segment
          var p0 = terminal.absolutePoints[seg];
          var pe = terminal.absolutePoints[seg + 1];
          var horizontal = p0.x - pe.x == 0;

          // Stores the segment in the edge state
          var key = source ? "sourceConstraint" : "targetConstraint";
          var value = horizontal ? "horizontal" : "vertical";
          edge.style[key] = value;

          // Keeps the coordinate within the segment bounds
          if (horizontal) {
            pt.x = p0.x;
            pt.y = Math.min(pt.y, Math.max(p0.y, pe.y));
            pt.y = Math.max(pt.y, Math.min(p0.y, pe.y));
          } else {
            pt.y = p0.y;
            pt.x = Math.min(pt.x, Math.max(p0.x, pe.x));
            pt.x = Math.max(pt.x, Math.min(p0.x, pe.x));
          }
        }
      }
      // Computes constraint connection points on vertices and ports
      else if (terminal != null && terminal.cell.geometry.relative) {
        pt = new mxPoint(
          this.getRoutingCenterX(terminal),
          this.getRoutingCenterY(terminal)
        );
      }

      // Snaps point to grid
      /*if (pt != null)
                {
                  var tr = this.graph.view.translate;
                  var s = this.graph.view.scale;
                  
                  pt.x = (this.graph.snap(pt.x / s - tr.x) + tr.x) * s;
                  pt.y = (this.graph.snap(pt.y / s - tr.y) + tr.y) * s;
                }*/
    }

    edge.setAbsoluteTerminalPoint(pt, source);
  };
}

function mxEdgeSegmentHandlerInitializer() {
  // Updates the terminal and control points in the cloned preview.
  mxEdgeSegmentHandler.prototype.clonePreviewState = function (
    point,
    terminal
  ) {
    var clone = mxEdgeHandler.prototype.clonePreviewState.apply(
      this,
      arguments
    );
    clone.cell = clone.cell.clone();

    if (this.isSource || this.isTarget) {
      clone.cell.geometry = clone.cell.geometry.clone();

      // Sets the terminal point of an edge if we're moving one of the endpoints
      if (this.graph.getModel().isEdge(clone.cell)) {
        // TODO: Only set this if the target or source terminal is an edge
        clone.cell.geometry.setTerminalPoint(point, this.isSource);
      } else {
        clone.cell.geometry.setTerminalPoint(null, this.isSource);
      }
    }

    return clone;
  };
}

function mxConstraintHandlerInit(isDarkMode){
  const highlightColor = isDarkMode ? "#00f0ff" : "#000000";
  mxConstraintHandler.prototype.createHighlightShape = function () {
    var shape = new mxEllipse(null, highlightColor, highlightColor);
    shape.dialect = mxConstants.DIALECT_SVG;
    shape.init(this.graph.getView().getOverlayPane());
    shape.node.style.pointerEvents = "none";
    return shape;
  };
}

function graphPrototypeInits(selectedEdgeColorRef , selectedEdgeWidthRef, isDarkMode) {
  // If connect preview is not moved away then getCellAt is used to detect the cell under
  // the mouse if the mouse is over the preview shape in IE (no event transparency), ie.
  // the built-in hit-detection of the HTML document will not be used in this case.

  mxGuideInit();
  mxConnectionHandlerInit(selectedEdgeColorRef , selectedEdgeWidthRef);
  mxGraphInit();
  mxEdgeSegmentHandlerInitializer();
  mxGraphViewInit();
  mxConstraintHandlerInit(isDarkMode);
  mxConstants.SHADOWCOLOR = "#C0C0C0";

  // Replaces the port image
  // mxConstraintHandler.prototype.pointImage = new mxImage(
  //   "/editors/images/point.gif",
  //   5,
  //   5
  // );

  // Edges have no connection points
  // mxPolyline.prototype.constraints = null;

  mxEdgeStyle.WireConnector = function (state, source, target, hints, result) {
    // Creates array of all way- and terminalpoints
    var pts = state.absolutePoints;
    var horizontal = true;
    var hint = null;

    // Gets the initial connection from the source terminal or edge
    if (source != null && state.view.graph.model.isEdge(source.cell)) {
      horizontal = state.style["sourceConstraint"] === "horizontal";
    } else if (source != null) {
      horizontal = source.style["portConstraint"] !== "vertical";

      // Checks the direction of the shape and rotates
      var direction = source.style[mxConstants.STYLE_DIRECTION];

      if (direction === "north" || direction === "south") {
        horizontal = !horizontal;
      }
    }

    // Adds the first point
    // TODO: Should move along connected segment
    var pt = pts[0];

    if (pt == null && source != null) {
      pt = new mxPoint(
        state.view.getRoutingCenterX(source),
        state.view.getRoutingCenterY(source)
      );
    } else if (pt != null) {
      pt = pt.clone();
    }

    var first = pt;

    // Adds the waypoints
    if (hints != null && hints.length > 0) {
      // FIXME: First segment not movable
      /*hint = state.view.transformControlPoint(state, hints[0]);
                  mxLog.show();
                  mxLog.debug(hints.length,'hints0.y='+hint.y, pt.y)
                  
                  if (horizontal && Math.floor(hint.y) != Math.floor(pt.y))
                  {
                    mxLog.show();
                    mxLog.debug('add waypoint');
            
                    pt = new mxPoint(pt.x, hint.y);
                    result.push(pt);
                    pt = pt.clone();
                    //horizontal = !horizontal;
                  }*/

      for (var i = 0; i < hints.length; i++) {
        horizontal = !horizontal;
        hint = state.view.transformControlPoint(state, hints[i]);

        if (horizontal) {
          if (pt.y != hint.y) {
            pt.y = hint.y;
            result.push(pt.clone());
          }
        } else if (pt.x != hint.x) {
          pt.x = hint.x;
          result.push(pt.clone());
        }
      }
    } else {
      hint = pt;
    }

    // Adds the last point
    pt = pts[pts.length - 1];

    // TODO: Should move along connected segment
    if (pt == null && target != null) {
      pt = new mxPoint(
        state.view.getRoutingCenterX(target),
        state.view.getRoutingCenterY(target)
      );
    }

    if (horizontal) {
      if (pt.y != hint.y && first.x != pt.x) {
        result.push(new mxPoint(pt.x, hint.y));
      }
    } else if (pt.x != hint.x && first.y != pt.y) {
      result.push(new mxPoint(hint.x, pt.y));
    }
  };

  mxStyleRegistry.putValue("wireEdgeStyle", mxEdgeStyle.WireConnector);
}

function graphHandlerInits(graph, selectedEdgeColorRef, selectedEdgeWidthRef) {
  mxEdgeHandlerInit(graph);
}

export { buildGraphObject, graphHandlerInits, graphPrototypeInits, mxConstraintHandlerInit };
